import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Package, Plus, Trash2, Upload, Loader2, Search, Download } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: 'panel', label: 'Solar Panels' },
  { key: 'inverter', label: 'Inverters' },
  { key: 'battery', label: 'Batteries' },
  { key: 'ev_charger', label: 'EV Chargers' },
  { key: 'consumer_unit', label: 'Consumer Units' },
];
const catLabel = (k) => CATEGORIES.find((c) => c.key === k)?.label || k;

const AdminProductCatalog = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ category: 'panel', manufacturer: '', model: '', sku: '' });
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get('/product-catalog')
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error('Failed to load catalog'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) =>
      (filter === 'all' || i.category === filter)
      && (!q || `${i.manufacturer} ${i.model} ${i.sku}`.toLowerCase().includes(q)));
  }, [items, filter, search]);

  const addItem = async () => {
    if (!newItem.manufacturer.trim()) { toast.error('Manufacturer is required'); return; }
    setAdding(true);
    try {
      await api.post('/product-catalog', newItem);
      toast.success('Added to catalog');
      setNewItem({ ...newItem, manufacturer: '', model: '', sku: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/product-catalog/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/product-catalog/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { added, skipped, errors } = r.data || {};
      toast.success(`Imported ${added} item(s)${skipped ? `, ${skipped} skipped` : ''}`);
      if (errors?.length) toast.warning(`${errors.length} row(s) had issues — check categories`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV import failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = 'Product,Manufacturer,Model,SKU\nPanel,JA Solar,JAM54S30-410/GR,\nInverter,GivEnergy,Gen3 Hybrid 5.0,\nBattery,GivEnergy,All in One,\nEV Charger,MyEnergi,Zappi v2.1,\nConsumer Unit,Hager,VML912CURK,\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'product-catalog-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const countByCat = (k) => items.filter((i) => i.category === k).length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="product-catalog-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package className="w-6 h-6 text-blue-600" /> Product Catalog
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manufacturers &amp; models that power the equipment dropdowns on customer files — keeps names consistent for stock checks.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" /> CSV template
            </Button>
            <input ref={fileRef} type="file" accept=".csv" onChange={onUpload} className="hidden" data-testid="csv-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload CSV
            </Button>
          </div>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <Badge key={c.key} variant="outline" className="text-xs">
              {c.label}: <span className="font-semibold ml-1">{countByCat(c.key)}</span>
            </Badge>
          ))}
        </div>

        {/* Add new */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Add a product</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div>
                <label className="text-xs text-slate-500">Section</label>
                <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                  <SelectTrigger data-testid="new-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Manufacturer" value={newItem.manufacturer} onChange={(e) => setNewItem({ ...newItem, manufacturer: e.target.value })} data-testid="new-manufacturer" />
              <Input placeholder="Model" value={newItem.model} onChange={(e) => setNewItem({ ...newItem, model: e.target.value })} data-testid="new-model" />
              <Input placeholder="SKU (optional)" value={newItem.sku} onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })} data-testid="new-sku" />
              <Button onClick={addItem} disabled={adding} data-testid="add-product-btn">
                {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="w-48">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sections</SelectItem>
                    {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input placeholder="Search manufacturer, model or SKU…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="catalog-search" />
              </div>
              <span className="text-sm text-slate-500">{filtered.length} item(s)</span>
            </div>

            {loading ? (
              <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm" data-testid="catalog-empty">
                No products yet — add one above or upload a CSV.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Section</TableHead>
                    <TableHead>Manufacturer</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((i) => (
                    <TableRow key={i.id} data-testid={`catalog-row-${i.id}`}>
                      <TableCell><Badge variant="secondary" className="text-xs">{catLabel(i.category)}</Badge></TableCell>
                      <TableCell className="font-medium">{i.manufacturer}</TableCell>
                      <TableCell>{i.model || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-slate-500">{i.sku || '—'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => remove(i.id)} data-testid={`delete-catalog-${i.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminProductCatalog;
